import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.path === from.path) {
      return false;
    }
    if (savedPosition) {
      return savedPosition;
    }
    return { left: 0, top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const { useSiteStore } = await import('@/stores/site');
  const siteStore = useSiteStore();
  if (!siteStore.loaded) {
    await siteStore.fetchSites();
  }

  const { useAdminAuthStore } = await import('@/stores/adminAuth');
  const adminAuthStore = useAdminAuthStore();
  adminAuthStore.hydrate();

  const requiresAuth = to.matched.some((record) => record.meta?.requiresAuth);

  if (requiresAuth && !adminAuthStore.isAuthenticated) {
    adminAuthStore.rememberRedirect(to.fullPath);
    const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
    next({ name: 'admin-login', params: { lang } });
    return;
  }

  // If authenticated and trying to access admin area
  if (requiresAuth && adminAuthStore.isAuthenticated) {
    // Ensure custom permissions are loaded
    if (adminAuthStore.customPermissions.length === 0) {
      await adminAuthStore.fetchCustomPermissions();
    }

    // Site access validation for site-scoped admin pages (exempting admin-unauthorized itself)
    const requiresSiteAccess = to.matched.some((record) => record.meta?.requiresSiteAccess);
    if (to.name !== 'admin-unauthorized' && requiresSiteAccess) {
      const selectedKey = siteStore.selectedSiteKey;
      const hasSiteAccess =
        !!selectedKey && adminAuthStore.userPermissions.includes(`site:${selectedKey}`);

      if (!hasSiteAccess) {
        const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
        next({ name: 'admin-unauthorized', params: { lang } });
        return;
      }
    }

    // Default redirect to a permitted page if hitting the admin root without proper rights
    if (to.name === 'admin-calendar' && !adminAuthStore.hasPermission('calendar')) {
      const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
      if (adminAuthStore.hasPermission('booking')) {
        next({ name: 'admin-service-desk', params: { lang } });
        return;
      } else if (adminAuthStore.hasPermission('lexware')) {
        next({ name: 'admin-invoices', params: { lang } });
        return;
      } else if (adminAuthStore.hasPermission('patients')) {
        next({ name: 'admin-patients', params: { lang } });
        return;
      } else if (adminAuthStore.roles.includes('role:ROLE_ADMIN')) {
        next({ name: 'admin-integrations', params: { lang } });
        return;
      } else {
        next({ name: 'admin-unauthorized', params: { lang } });
        return;
      }
    }

    const requiresRole = to.matched.map((r) => r.meta?.requiresRole).find((p) => p);
    if (requiresRole && !adminAuthStore.roles.includes(requiresRole as string)) {
      const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
      next({ name: 'admin-unauthorized', params: { lang } });
      return;
    }

    // Site-specific Lexware enablement validation
    if (to.name === 'admin-invoices' && !siteStore.isLexwareEnabled) {
      const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
      if (adminAuthStore.hasPermission('calendar')) {
        next({ name: 'admin-calendar', params: { lang } });
      } else if (adminAuthStore.hasPermission('booking')) {
        next({ name: 'admin-service-desk', params: { lang } });
      } else {
        next({ name: 'admin-unauthorized', params: { lang } });
      }
      return;
    }

    // Site-specific Booking enablement validation (calendar, service-desk, mappings all require booking)
    if (
      (to.name === 'admin-calendar' ||
        to.name === 'admin-service-desk' ||
        to.name === 'admin-mappings') &&
      !siteStore.isBookingEnabled
    ) {
      const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
      if (siteStore.isLexwareEnabled && adminAuthStore.hasPermission('lexware')) {
        next({ name: 'admin-invoices', params: { lang } });
      } else if (adminAuthStore.roles.includes('role:ROLE_ADMIN')) {
        next({ name: 'admin-integrations', params: { lang } });
      } else {
        next({ name: 'admin-unauthorized', params: { lang } });
      }
      return;
    }

    const requiresPermission = to.matched.map((r) => r.meta?.requiresPermission).find((p) => p);
    if (requiresPermission && !adminAuthStore.hasPermission(requiresPermission as string)) {
      const lang = typeof to.params.lang === 'string' ? to.params.lang : 'de';
      next({ name: 'admin-unauthorized', params: { lang } });
      return;
    }
  }

  if (to.params.lang && (to.params.lang === 'en' || to.params.lang === 'de')) {
    const { i18nInstance } = await import('@/plugins/i18n');
    i18nInstance.global.locale.value = to.params.lang;
  }

  next();
});

export default router;
